import React, { Component } from "react";
import Form from "react-bootstrap/Form";
import _ from "lodash";
import Loading from "../../common/components/loading/loading";
import { getRegistry } from "../../common/services/albertaEnvironmental/albertaEnvironmentalService";
import SearchBox from "../../common/components/search/searchBox";
import { formatDate } from "../../common/services/utilities";
import { withTranslation } from "react-i18next";
import AlbertaEnvironmentalRegistryTable from "./albertaEnvironmentalRegistryTable";
import Input from "../../common/components/form/input";
import Select from "../../common/components/form/select";
import * as XLSX from "xlsx";
import FontAwesome from "react-fontawesome";
import LifecycleChart from "./lifecycleChart";
import PieChart from "./pieChart";

const AEOR_STATUS_VALUES = [
  "Retired",
  "Retired for Compliance",
  "Pending Retirement",
  "Pending Retirement Compliance CSA Approval",
  "Pending Retirement Federal OBPS CSA Approval",
  "Pending Retire - Federal OBPS",
  "Active",
  "Removed",
  "Revoked",
  "Cancelled",
  "Locked In Transaction",
  "Retire - Federal OBPS",
];

const AEOR_COLOR_SCHEME = [
  "grey",
  "darkgrey",
  "orange",
  "darkorange",
  "goldenrod",
  "gold",
  "darkgreen",
  "black",
  "red",
  "darkred",
  "green",
  "darkblue",
];

const EPC_STATUS_VALUES = [
  "Retired",
  "Retired for Compliance",
  "Pending Retirement Compliance CSA Approval",
  "Pending Retirement Voluntary CSA Approval",
  "Pending Retirement",
  "Active",
  "Cancelled",
  "Locked In Transaction",
];

const EPC_COLOR_SCHEME = [
  "grey",
  "darkgrey",
  "goldenrod",
  "darkorange",
  "orange",
  "darkgreen",
  "darkred",
  "green",
];

class AlbertaEnvironmentalRegistry extends Component {
  filteredRegistryCache = null;

  state = {
    registry: [],
    searchQuery: "",
    aeor: true,
    epc: true,
    registryDate: "",
    minDate: formatDate(new Date("2023-09-01")), // Hard coded, earliest date that we started scraping
    maxDate: formatDate(new Date()),
    quantificationProtocols: [],
    quantificationProtocol: "All",
    vintages: [],
    vintage: "All",
    statuses: [],
    status: "All",
    aeorVintages: [],
    epcVintages: [],
    sortColumn: { path: "TransactionId", order: "desc" },
    isLoading: true,
    reloading: false,
  };

  async componentDidMount() {
    await this.loadData();
  }

  prepareRegistryRecord = (record) => ({
    ...record,
    _searchText: [
      record.CurrentOwner,
      record.Title,
      record.EmissionOffsetProjectDeveloper,
      record.Facility,
      record.ProjectIdentifier,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase(),
    _activityStartDisplay: record.ActivityStart
      ? formatDate(record.ActivityStart)
      : null,
    _expiryDateDisplay: record.ExpiryDate ? formatDate(record.ExpiryDate) : null,
    _offsetStartDateDisplay: record.OffsetStartDate
      ? formatDate(record.OffsetStartDate)
      : null,
    _offsetEndDateDisplay: record.OffsetEndDate
      ? formatDate(record.OffsetEndDate)
      : null,
  });

  async loadData(registryDate = null) {
    const registry = (await getRegistry(registryDate)).map(
      this.prepareRegistryRecord
    );

    const quantificationProtocols = [{ quantificationProtocol: "All" }].concat(
      [
        ...new Set(
          registry
            .filter((x) => x.QuantificationProtocol)
            .map((x) => x.QuantificationProtocol)
        ),
      ]
        .sort()
        .map((x) => ({ quantificationProtocol: x }))
    );
    const statuses = [{ status: "All" }].concat(
      [...new Set(registry.filter((x) => x.Status).map((x) => x.Status))]
        .sort()
        .map((x) => ({ status: x }))
    );
    const vintages = [{ vintage: "All" }].concat(
      [...new Set(registry.filter((x) => x.Vintage).map((x) => x.Vintage))]
        .sort()
        .map((x) => ({ vintage: x }))
    );
    const aeorVintages = Array.from(
      new Set(
        registry
          .filter((u) => u.Registry === "AEOR")
          .map((item) => item.Vintage)
      )
    ).sort((a, b) => a - b);
    const epcVintages = Array.from(
      new Set(
        registry
          .filter((u) => u.Registry === "Emission Performance Credit")
          .map((item) => item.Vintage)
      )
    ).sort((a, b) => a - b);

    this.filteredRegistryCache = null;

    this.setState({
      registry,
      isLoading: false,
      reloading: false,
      quantificationProtocols,
      statuses,
      vintages,
      aeorVintages,
      epcVintages,
    });
  }

  handleRegistryDateChange = (registryDate) => {
    this.setState({ registryDate: registryDate, reloading: true });

    (async () => {
      await this.loadData(registryDate);
    })();
  };

  handleSearch = (query) => {
    this.setState({ searchQuery: query });
  };

  handleSort = (sortColumn) => {
    this.setState({ sortColumn });
  };

  handleQuantificationProtocolChange = (quantificationProtocol) => {
    this.setState({ quantificationProtocol: quantificationProtocol });
  };

  handleVintageChange = (vintage) => {
    this.setState({ vintage: vintage });
  };

  handleStatusChange = (status) => {
    this.setState({ status: status });
  };

  handleToggleAeor = () => {
    this.setState({ aeor: !this.state.aeor });
  };

  handleToggleEpc = () => {
    this.setState({ epc: !this.state.epc });
  };

  downloadExcel = (data) => {
    const excelData = data.map((record) => ({
      ProjectIdentifier: record.ProjectIdentifier,
      CurrentOwner: record.CurrentOwner,
      EmissionOffsetProjectDeveloper: record.EmissionOffsetProjectDeveloper,
      Quantity: record.Quantity,
      Registry: record.Registry,
      Vintage: record.Vintage,
      QuantificationProtocol: record.QuantificationProtocol,
      ActivityStart: record.ActivityStart
        ? formatDate(record.ActivityStart)
        : null,
      ComplianceYear: record.ComplianceYear,
      ExpiryDate: record.ExpiryDate ? formatDate(record.ExpiryDate) : null,
      Facility: record.Facility,
      OffsetStartDate: record.OffsetStartDate
        ? formatDate(record.OffsetStartDate)
        : null,
      OffsetEndDate: record.OffsetEndDate
        ? formatDate(record.OffsetEndDate)
        : null,
      Status: record.Status,
      Title: record.Title,
      VerifierCompany: record.VerifierCompany,
      SerialStart: record.SerialStart,
      SerialEnd: record.SerialEnd,
      TransactionId: record.TransactionId,
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, "AlbertaEnvironmentalRegistry.xlsx");
  };

  getRegistryViewData = () => {
    const {
      searchQuery,
      sortColumn,
      epc,
      aeor,
      // startDate,
      // endDate,
      quantificationProtocol,
      vintage,
      status,
      registry: allRegistry,
    } = this.state;

    const normalizedSearchQuery = searchQuery.trim().toLowerCase();
    const cache = this.filteredRegistryCache;

    if (
      cache &&
      cache.registry === allRegistry &&
      cache.searchQuery === normalizedSearchQuery &&
      cache.sortPath === sortColumn.path &&
      cache.sortOrder === sortColumn.order &&
      cache.epc === epc &&
      cache.aeor === aeor &&
      cache.quantificationProtocol === quantificationProtocol &&
      cache.vintage === vintage &&
      cache.status === status
    ) {
      return cache.value;
    }

    const parsedVintage =
      vintage === "All" ? null : Number.parseInt(vintage, 10);
    const filteredRegistry = [];
    const aeorData = [];
    const epcData = [];

    for (const record of allRegistry) {
      if (!epc && record.Registry === "Emission Performance Credit") {
        continue;
      }

      if (!aeor && record.Registry === "AEOR") {
        continue;
      }

      if (
        quantificationProtocol !== "All" &&
        record.QuantificationProtocol !== quantificationProtocol
      ) {
        continue;
      }

      if (status !== "All" && record.Status !== status) {
        continue;
      }

      if (
        parsedVintage !== null &&
        Number.parseInt(record.Vintage, 10) !== parsedVintage
      ) {
        continue;
      }

      if (
        normalizedSearchQuery &&
        !record._searchText.includes(normalizedSearchQuery)
      ) {
        continue;
      }

      filteredRegistry.push(record);

      if (record.Registry === "AEOR") {
        aeorData.push(record);
      } else if (record.Registry === "Emission Performance Credit") {
        epcData.push(record);
      }
    }

    const sortedRegistry = _.orderBy(
      filteredRegistry,
      [sortColumn.path],
      [sortColumn.order]
    );
    const value = {
      totalCount: filteredRegistry.length,
      tableData: sortedRegistry,
      aeorData,
      epcData,
    };

    this.filteredRegistryCache = {
      registry: allRegistry,
      searchQuery: normalizedSearchQuery,
      sortPath: sortColumn.path,
      sortOrder: sortColumn.order,
      epc,
      aeor,
      quantificationProtocol,
      vintage,
      status,
      value,
    };

    return value;
  };

  render() {
    const {
      searchQuery,
      sortColumn,
      aeor,
      epc,
      registryDate,
      minDate,
      maxDate,
      quantificationProtocols,
      quantificationProtocol,
      vintages,
      vintage,
      statuses,
      status,
      aeorVintages,
      epcVintages,
      isLoading,
      reloading,
    } = this.state;

    if (isLoading) return <Loading />;

    const { t, isSubscriber } = this.props;

    const { totalCount, tableData, aeorData, epcData } =
      this.getRegistryViewData();

    const contactEmail = process.env.REACT_APP_CONTACT_EMAIL;

    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12 d-flex align-items-center justify-content-center">
            <h1>{t("AlbertaEnvironmentalRegistry")}</h1>
          </div>
          <div className="row">
            <div className="col-md-2" style={{ alignSelf: "flex-end" }}>
              <SearchBox
                value={searchQuery}
                onChange={this.handleSearch}
                disabled={!isSubscriber}
              />
            </div>
            <div className="col-md-2">
              <Input
                type="date"
                min={minDate}
                max={maxDate}
                onKeyDown={(e) => e.preventDefault()}
                onFocus={(e) => e.blur()}
                onChange={(e) =>
                  this.handleRegistryDateChange(e.currentTarget.value)
                }
                value={registryDate}
                label={t("RegistryDate")}
                disabled={!isSubscriber}
              />
            </div>
            <div className="col-md-2">
              <Select
                idField="status"
                optionField="status"
                name="status"
                //disabled={Object.keys(organizations).length < 2}
                value={status}
                label={t("Status")}
                options={statuses}
                onChange={(e) => this.handleStatusChange(e.currentTarget.value)}
                disabled={!isSubscriber}
              />
            </div>
            <div className="col-md-1">
              <Select
                idField="vintage"
                optionField="vintage"
                name="vintage"
                //disabled={Object.keys(organizations).length < 2}
                value={vintage}
                label={t("Vintage")}
                options={vintages}
                onChange={(e) =>
                  this.handleVintageChange(e.currentTarget.value)
                }
                disabled={!isSubscriber}
              />
            </div>
            <div className="col-md-1" style={{ alignSelf: "flex-end" }}>
              <Form.Switch
                className="switch"
                type="switch"
                label={t("Aeor")}
                checked={aeor}
                value={aeor}
                onChange={this.handleToggleAeor}
                disabled={!isSubscriber}
              />
            </div>
            <div className="col-md-1" style={{ alignSelf: "flex-end" }}>
              <Form.Switch
                className="switch"
                type="switch"
                label={t("Epc")}
                checked={epc}
                value={epc}
                onChange={this.handleToggleEpc}
                disabled={!isSubscriber}
              />
            </div>

            <div className="col-md-2">
              <Select
                idField="quantificationProtocol"
                optionField="quantificationProtocol"
                name="quantificationProtocol"
                //disabled={Object.keys(organizations).length < 2}
                value={quantificationProtocol}
                label={t("QuantificationProtocol")}
                options={quantificationProtocols}
                onChange={(e) =>
                  this.handleQuantificationProtocolChange(e.currentTarget.value)
                }
                disabled={!isSubscriber}
              />
            </div>
            <div className="col-md-1" style={{ alignSelf: "flex-end" }}>
              <button
                className="btn btn-sm btn-primary"
                onClick={() => this.downloadExcel(tableData)}
                disabled={!isSubscriber}
              >
                <FontAwesome name="download" /> {t("Excel")}
              </button>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-2">
            <p>
              {t("RetrievedAlbertaEnvironmentalRegistry", {
                count: totalCount,
              })}
            </p>
          </div>
          {!isSubscriber && (
            <div
              className="col-md-8 d-flex align-items-center justify-content-center"
              style={{ fontStyle: "italic", color: "darkred" }}
            >
              <p>
                {t("SubscriptionInquiry")}{" "}
                <a href={"mailto:" + contactEmail}>{contactEmail}</a>
              </p>
            </div>
          )}
        </div>
        {!reloading && (
          <>
            <div className="row">
              <div className="col-md-3 tile">
                <PieChart
                  data={aeorData}
                  title="Offsets by Owner"
                  groupBy="CurrentOwner"
                />
              </div>
              <div className="col-md-3 tile">
                <PieChart
                  data={aeorData}
                  title="Offsets by Developer"
                  groupBy="EmissionOffsetProjectDeveloper"
                />
              </div>
              <div className="col-md-3 tile">
                <PieChart
                  data={aeorData}
                  title="Offsets by Protocol"
                  groupBy="QuantificationProtocol"
                />
              </div>
              <div className="col-md-3 tile">
                <PieChart
                  data={epcData}
                  title="EPCs By Owner"
                  groupBy="CurrentOwner"
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 tile">
                <LifecycleChart
                  data={aeorData}
                  title="AEOR Status by Vintage"
                  uniqueStatusValues={AEOR_STATUS_VALUES}
                  colorScheme={AEOR_COLOR_SCHEME}
                  vintages={aeorVintages}
                />
              </div>
              <div className="col-md-6 tile">
                <LifecycleChart
                  data={epcData}
                  title="EPC Status by Vintage"
                  uniqueStatusValues={EPC_STATUS_VALUES}
                  colorScheme={EPC_COLOR_SCHEME}
                  vintages={epcVintages}
                />
              </div>
            </div>
            <div className="col-md-12" style={{ paddingBottom: "30px" }}>
              <AlbertaEnvironmentalRegistryTable
                registry={tableData}
                sortColumn={sortColumn}
                onSort={this.handleSort}
                //onRowClick={this.handleTradeSelected}
              />
            </div>
          </>
        )}
        {reloading && <Loading />}
      </div>
    );
  }
}

export default withTranslation(["marketdata"])(AlbertaEnvironmentalRegistry);
